import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    FormControlLabel,
    Grid,
    Chip,
    Autocomplete,
    CircularProgress,
    Alert,
    InputAdornment,
    Paper,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ColDef } from 'ag-grid-community';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './UserManagement.scss';
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Storefront as StorefrontIcon,
    WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';

interface Permission {
    read: boolean;
    write: boolean;
    delete: boolean;
}

interface Permissions {
    [key: string]: Permission;
}

interface RestaurantRole {
    role: string;
    permissions: Permissions;
}

interface Roles {
    [restaurantId: string]: RestaurantRole;
}

interface User {
    id: string;
    name: string;
    email: string;
    isTemp: boolean;
    phone: string;
    image: string;
    roles: Roles;
    createdAt: any
}

interface Restaurant {
    id: string;
    name: string;
    // Add other restaurant fields as needed
}

const AVAILABLE_PERMISSIONS = {
    printKOT: 'Print KOT',
    testBilling: 'Test Billing',
    updateOrder: 'Update Order',
    reports: 'Reports',
    analytics: 'Analytics',
    roomBookings: 'Room Bookings',
    onlineOrders: 'Online Orders',
    uploadGuestImages: 'Upload Guest Images',
    // searchFood: 'Search Food',

    // Add other permissions as needed
};

const ROLES = ['admin', 'manager', 'staff'];

const UserManagement = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        isTemp: true,
        phone: '',
        roles: {} as Roles,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
    const [selectedRestaurants, setSelectedRestaurants] = useState<Restaurant[]>([]);
    const [loadingRestaurants, setLoadingRestaurants] = useState(false);

    const handleShareLoginLink = (phone: string, name: string) => {
        const loginUrl = `${window.location.origin}/login`;
        const message = `Hello ${name}, \nYou can log in to our POS system using your phone number ${phone} by clicking the link below:\n\n*POS Site Link*: ${loginUrl}`;
        const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    // Update column definitions with proper typing
    const columnDefs: ColDef<User>[] = [
        { 
            field: 'name' as keyof User, 
            headerName: 'Name', 
            sortable: true, 
            filter: true 
        },
        { 
            field: 'email' as keyof User, 
            headerName: 'Email', 
            sortable: true, 
            filter: true 
        },
        { 
            field: 'phone' as keyof User, 
            headerName: 'Phone', 
            sortable: true, 
            filter: true 
        },
        { 
            field: 'roles' as keyof User, 
            headerName: 'Roles', 
            sortable: true, 
            filter: true,
            cellRenderer: (params: { value: Roles }) => {
                if(!params?.value || !Object.entries(params?.value)?.length){
                    return '';
                }
                return Object.entries(params.value).map(([restaurantId, roleData]) => (
                    <Chip key={restaurantId} label={roleData?.role?.charAt(0).toUpperCase() + roleData?.role?.slice(1)} />
                ));
            }
        },
        {
            headerName: 'Actions',
            field: 'id' as keyof User,
            sortable: false,
            filter: false,
            minWidth: 150,
            cellRenderer: (params: { data: User; value: string }) => {
                return (
                    <div className="action-buttons">
                        <Button
                            size="small"
                            color="success"
                            onClick={() => handleShareLoginLink(params?.data?.phone, params?.data?.name)}
                        >
                            <WhatsAppIcon fontSize="small" />
                        </Button>
                        <Button
                            size="small"
                            onClick={() => handleOpenDialog(params?.data)}
                        >
                            <EditIcon fontSize="small" />
                        </Button>
                        <Button
                            size="small"
                            color="error"
                            onClick={() => handleDelete(params?.value)}
                        >
                            <DeleteIcon fontSize="small" />
                        </Button>
                    </div>
                );
            }
        }
    ];

    // Update default column definition with proper typing
    const defaultColDef: ColDef<User> = {
        flex: 1,
        minWidth: 100,
        resizable: true,
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const db = getFirestore();
            const usersCollection = collection(db, 'users');
            const userSnapshot = await getDocs(usersCollection);
            const userList = userSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as User));
            setUsers(userList);
        } catch (err: any) {
            setError(err.message);
        }
        setLoading(false);
    };

    const fetchRestaurants = async () => {
        setLoadingRestaurants(true);
        try {
            const db = getFirestore();
            const restaurantsSnapshot = await getDocs(collection(db, 'restaurants'));
            const restaurantsList = restaurantsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as Restaurant));
            setRestaurants(restaurantsList);
        } catch (err) {
            console.error('Error fetching restaurants:', err);
        }
        setLoadingRestaurants(false);
    };

    useEffect(() => {
        fetchUsers();
        fetchRestaurants();
    }, []);

    const handleOpenDialog = (user?: User) => {
        if (user) {
            setSelectedUser(user);
            setFormData({
                name: user?.name || '',
                email: user?.email || '',
                isTemp: !!user?.isTemp,
                phone: user?.phone || '',
                roles: user?.roles || {},
            });
            setSelectedRestaurants(restaurants.filter(restaurant => user?.roles?.hasOwnProperty(restaurant?.id) && user?.roles[restaurant?.id]?.role));
        } else {
            setSelectedUser(null);
            setFormData({
                name: '',
                email: '',
                isTemp: true,
                phone: '',
                roles: {} as Roles,
            });
            setSelectedRestaurants([]);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedUser(null);
        setError('');
    };

    const handleSubmit = async () => {
        if (selectedUser) {
            // Handle existing user update
            setLoading(true);
            try {
                const db = getFirestore();
                const userData = {
                    ...formData,
                    updatedAt: Date.now(),
                };
                await updateDoc(doc(db, 'users', selectedUser.id), userData);
                handleCloseDialog();
                fetchUsers();
            } catch (err: any) {
                setError(err.message);
            }
            setLoading(false);
        } else {
            // For new user, start phone verification process
            // Handle existing user update
            setLoading(true);
            try {
                const db = getFirestore();
                const userData = {
                    ...formData,
                    phone: `+91${formData?.phone}`,
                    createdAt: Date.now(),
                };
                if(userData?.phone){
                    console.log(userData);
                    await setDoc(doc(db, 'users', userData?.phone), userData);
                    handleCloseDialog();
                    fetchUsers();
                }
            } catch (err: any) {
                setError(err.message);
            }
            setLoading(false);
        }
    };

    const handleDelete = async (userId: string) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            setLoading(true);
            try {
                const db = getFirestore();
                await deleteDoc(doc(db, 'users', userId));
                fetchUsers();
            } catch (err: any) {
                setError(err.message);
            }
            setLoading(false);
        }
    };


    return (
        <Container className="user-management-container" sx={{p: 0, overflow: 'scroll'}}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, p: 0 }}>
                <Typography variant="h6">Users</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Add User
                </Button>
            </Box>

            <div 
                className="ag-theme-alpine" 
                style={{ 
                    height: 'calc(100vh - 200px)', 
                    width: '100%' 
                }}
            >
                <AgGridReact<User>
                    rowData={users}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    animateRows={true}
                    rowSelection='single'
                    pagination={true}
                    paginationPageSize={10}
                    suppressCellFocus={true}
                />
            </div>

            <UserDialog 
                open={openDialog}
                onClose={handleCloseDialog}
                user={selectedUser}
                loading={loading}
                error={error}
                formData={formData}
                setFormData={setFormData}
                onSubmit={handleSubmit}
                restaurants={restaurants}
                selectedRestaurants={selectedRestaurants}
                setSelectedRestaurants={setSelectedRestaurants}
                loadingRestaurants={loadingRestaurants}
            />
        </Container>
    );
};

const UserDialog = ({ 
    open, 
    onClose, 
    user, 
    loading, 
    error,
    formData,
    setFormData,
    onSubmit,
    restaurants,
    selectedRestaurants,
    setSelectedRestaurants,
    loadingRestaurants 
}: any) => {

    const handleRoleChange = (restaurantId: string, role: any) => {
        setFormData((prev: any) => ({
            ...prev,
            roles: {
                ...prev.roles,
                [restaurantId]: {
                    ...prev.roles?.[restaurantId],
                    role
                }
            }
        }));
    };
    const handlePermissionChange = (
        restaurantId: string,
        permissionKey: string,
        action: keyof any,
        checked: boolean
    ) => {
        setFormData((prev: any) => ({
            ...prev,
            roles: {
                ...prev.roles,
                [restaurantId]: {
                    ...prev.roles?.[restaurantId],
                    permissions: {
                        ...prev.roles?.[restaurantId]?.permissions,
                        [permissionKey]: {
                            ...prev.roles?.[restaurantId]?.permissions?.[permissionKey],
                            [action]: checked
                        }
                    }
                }
            }
        }));
    };
    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    padding: '16px'
                }
            }}
        >
            <DialogTitle sx={{ 
                pb: 2, 
                borderBottom: '1px solid rgba(0,0,0,0.12)',
                display: 'flex',
                alignItems: 'center',
                gap: 1
            }}>
                {user ? (
                    <>
                        <EditIcon color="primary" />
                        <Typography variant="h6">Edit User Details</Typography>
                    </>
                ) : (
                    <>
                        <AddIcon color="primary" />
                        <Typography variant="h6">Add New User</Typography>
                    </>
                )}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Basic Information
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Full Name"
                                placeholder="Enter user's full name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                type="email"
                                placeholder="Enter email address"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                placeholder="Enter 10-digit phone number"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PhoneIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Restaurant Access
                    </Typography>
                    <Autocomplete
                        multiple
                        options={restaurants}
                        loading={loadingRestaurants}
                        getOptionLabel={(option) => option.name}
                        value={selectedRestaurants}
                        onChange={(_, newValue) => setSelectedRestaurants(newValue)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select restaurants"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <>
                                            <InputAdornment position="start">
                                                <StorefrontIcon color="action" />
                                            </InputAdornment>
                                            {params.InputProps.startAdornment}
                                        </>
                                    ),
                                    endAdornment: (
                                        <>
                                            {loadingRestaurants ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option.name}
                                    {...getTagProps({ index })}
                                    sx={{ 
                                        backgroundColor: 'primary.light',
                                        '& .MuiChip-deleteIcon': {
                                            color: 'primary.main'
                                        }
                                    }}
                                />
                            ))
                        }
                    />
                </Box>

                <Box>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Role & Permissions
                    </Typography>
                    {selectedRestaurants.map((restaurant: any) => (
                        <Accordion 
                            key={restaurant.id}
                            sx={{
                                '&:before': { display: 'none' },
                                boxShadow: 'none',
                                border: '1px solid rgba(0,0,0,0.12)',
                                mb: 2,
                                borderRadius: '8px !important',
                            }}
                        >
                            <AccordionSummary 
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ 
                                    backgroundColor: 'rgba(0,0,0,0.02)',
                                    borderRadius: '8px'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <StorefrontIcon color="action" fontSize="small" />
                                    <Typography>{restaurant.name}</Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ mb: 3 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Role</InputLabel>
                                        <Select
                                            value={formData.roles?.[restaurant.id]?.role || 'staff'}
                                            onChange={(e: any) => handleRoleChange(restaurant.id, e.target.value)}
                                            // sx={{ minWidth: 200 }}
                                            fullWidth
                                        >
                                            {ROLES.map(role => (
                                                <MenuItem key={role} value={role}>
                                                    {role.charAt(0).toUpperCase() + role.slice(1)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Permissions
                                </Typography>
                                {Object.entries(AVAILABLE_PERMISSIONS).map(([key, label]) => (
                                    <Paper 
                                        key={key} 
                                        elevation={0}
                                        sx={{ 
                                            p: 2, 
                                            mb: 2,
                                            border: '1px solid rgba(0,0,0,0.08)',
                                            borderRadius: 2
                                        }}
                                    >
                                        <Typography variant="body2" gutterBottom>
                                            {label}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                            {['read', 'write', 'delete'].map((action) => (
                                                <FormControlLabel
                                                    key={action}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={formData.roles?.[restaurant.id]?.permissions?.[key]?.[action] || false}
                                                            onChange={(e) => handlePermissionChange(restaurant.id, key, action, e.target.checked)}
                                                        />
                                                    }
                                                    label={action.charAt(0).toUpperCase() + action.slice(1)}
                                                />
                                            ))}
                                        </Box>
                                    </Paper>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.12)' }}>
                <Button 
                    onClick={onClose}
                    variant="outlined"
                    startIcon={<CloseIcon />}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={onSubmit}
                    variant="contained"
                    disabled={loading}
                    startIcon={user ? <SaveIcon /> : <AddIcon />}
                >
                    {loading ? (
                        <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            {user ? 'Updating...' : 'Creating...'}
                        </>
                    ) : (
                        user ? 'Update User' : 'Create User'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserManagement;
